import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import * as Dialog from '@radix-ui/react-dialog';
import {
  CommandLineIcon,
  TrashIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, ExclamationTriangleIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/solid';
import { apiClient } from '../../lib/axios';
import { baseURL } from '../../config/apiConfig';

interface ApiKey {
  id: string;
  name: string;
  last_used_at: string | null;
  token: string;
  created_at: string;
}

interface ApiLog {
  id: string;
  method: string;
  endpoint: string;
  status_code: number;
  request_payload: any;
  response_payload: any;
  created_at: string;
  api_key_id: string;
  api_key: { name: string };
}

interface Analytics {
  total_requests: number;
  success_count: number;
  success_rate: number;
  requests_by_day: { date: string; count: number; success_count: number }[];
}

interface PaginatedLogs {
  data: ApiLog[];
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

function AnalyticsPanel({ keyId }: { keyId: string }) {
  const [data, setData] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiClient.get(`/user/keys/${keyId}/analytics`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [keyId]);

  if (loading) {
    return (
      <div className="grid grid-cols-3 gap-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-slate-50 rounded-2xl p-5 h-24 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!data) return null;

  const maxDay = Math.max(...(data.requests_by_day.map(d => d.count)), 1);

  return (
    <div className="space-y-5">
      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-5 text-center">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-wide mb-1">Total Requests</p>
          <p className="text-3xl font-extrabold text-slate-900">{data.total_requests.toLocaleString()}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5 text-center">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-wide mb-1">Successful</p>
          <p className="text-3xl font-extrabold text-emerald-600">{data.success_count.toLocaleString()}</p>
        </div>
        <div className={`rounded-2xl p-5 text-center ${data.success_rate >= 90 ? 'bg-emerald-50 border border-emerald-100' : data.success_rate >= 70 ? 'bg-amber-50 border border-amber-100' : 'bg-red-50 border border-red-100'}`}>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-wide mb-1">Success Rate</p>
          <p className={`text-3xl font-extrabold ${data.success_rate >= 90 ? 'text-emerald-600' : data.success_rate >= 70 ? 'text-amber-600' : 'text-red-600'}`}>{data.success_rate}%</p>
        </div>
      </div>

      {/* 7-day bar chart */}
      {data.requests_by_day.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-wide mb-4">Requests, Last 7 Days</p>
          <div className="flex items-end gap-2 h-24">
            {data.requests_by_day.map(d => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
                <div className="w-full flex flex-col justify-end h-20 relative">
                  <div
                    className="w-full bg-brand-500 rounded-t-md transition-all duration-300 group-hover:bg-brand-600"
                    style={{ height: `${Math.round((d.count / maxDay) * 100)}%`, minHeight: d.count > 0 ? '4px' : '0' }}
                  />
                </div>
                <span className="text-[9px] font-bold text-slate-400">{new Date(d.date + 'T00:00:00').toLocaleDateString('en', { weekday: 'short' })}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function APIAccess() {
  useDocumentTitle('API Access', 'Manage your Localcontact API keys, view request logs, and integrate with your own applications.');
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [paginatedLogs, setPaginatedLogs] = useState<PaginatedLogs | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newlyCreatedToken, setNewlyCreatedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [keyToDelete, setKeyToDelete] = useState<ApiKey | null>(null);
  const [filterKeyId, setFilterKeyId] = useState<string>('all');
  const [analyticsKeyId, setAnalyticsKeyId] = useState<string | null>(null);
  const [docTab, setDocTab] = useState<'single' | 'bulk'>('single');
  const logsPerPage = 20;

  const fetchData = async (isRefresh = false, page = 1) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const [keysRes, logsRes] = await Promise.all([
        apiClient.get('/user/keys'),
        apiClient.get('/user/api-logs', { params: { page, per_page: logsPerPage } })
      ]);
      setKeys(keysRes.data);
      setPaginatedLogs(logsRes.data);
    } catch (err) {
      console.error("Failed to fetch API data", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateKey = async () => {
    if (!newKeyName) return;
    try {
      const res = await apiClient.post('/user/keys', { name: newKeyName });
      setNewlyCreatedToken(res.data.raw_token);
      setNewKeyName('');
      fetchData(true);
    } catch (err) {}
  };

  const handleDeleteKey = async () => {
    if (!keyToDelete) return;
    try {
      await apiClient.delete(`/user/keys/${keyToDelete.id}`);
      setKeyToDelete(null);
      fetchData(true);
      if (filterKeyId === keyToDelete.id) setFilterKeyId('all');
    } catch (err) {}
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCreateClose = (open: boolean) => {
    setIsCreateOpen(open);
    if (!open) {
      setTimeout(() => {
        setNewlyCreatedToken(null);
        setNewKeyName('');
      }, 300);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const allLogs = paginatedLogs?.data || [];
  const filteredLogs = filterKeyId === 'all' ? allLogs : allLogs.filter(log => log.api_key_id === filterKeyId);
  const totalLogs = paginatedLogs?.total || 0;
  const currentPage = paginatedLogs?.current_page || 1;
  const lastPage = paginatedLogs?.last_page || 1;

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      fetchData(false, currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < lastPage) {
      fetchData(false, currentPage + 1);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <style>{`
        @keyframes overlayShow { from { opacity: 0; } to { opacity: 1; } }
        @keyframes contentShow { 
          from { opacity: 0; transform: translate(-50%, -45%) scale(0.95); } 
          to { opacity: 1; transform: translate(-50%, -50%) scale(1); } 
        }
        .dialog-overlay {
          background-color: rgba(15, 23, 42, 0.6);
          backdrop-filter: blur(4px);
          position: fixed; inset: 0; z-index: 100;
          animation: overlayShow 200ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .dialog-content {
          background-color: white; border-radius: 32px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
          width: 90vw; max-width: 450px; max-height: 85vh; padding: 32px;
          z-index: 101; animation: contentShow 300ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .dialog-content:focus { outline: none; }
      `}</style>

      <div className="flex-1 px-4 sm:px-8 lg:px-10 py-6 sm:py-10 overflow-y-auto">
        <div className="max-w-[1400px] mx-auto flex flex-col gap-8">
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
               <div>
                <h1 className="text-[32px] font-extrabold text-slate-900 tracking-tight">API Access</h1>
                <p className="text-[16px] text-slate-500 mt-1 font-medium">Manage credentials, review docs, and monitor live traffic.</p>
              </div>
              {refreshing && (
                <div className="flex items-center gap-2 bg-brand-50 text-slate-800 px-3 py-1 rounded-full animate-pulse border border-brand-100">
                  <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                  <span className="text-[11px] font-bold uppercase tracking-wider">Syncing</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Link
                to="/dashboard/api-analytics"
                className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-5 py-2.5 rounded-xl text-[14px] font-bold hover:bg-slate-50 transition-colors shadow-sm"
              >
                <ChartBarIcon className="w-4 h-4" /> View Analytics
              </Link>
              <button onClick={() => setIsCreateOpen(true)} className="flex items-center gap-2 bg-brand-500 text-slate-900 px-6 py-2.5 rounded-xl text-[14px] font-bold hover:bg-brand-600 transition-colors shadow-lg shadow-brand-500/25">
                <PlusIcon className="w-5 h-5" /> Create Secret Key
              </button>
            </div>
          </div>

          {/* Key Management Table */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col overflow-hidden">
            <div className="px-4 sm:px-8 py-4 sm:py-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <h3 className="text-[16px] font-bold text-slate-900">Active Secret Keys</h3>
              <span className="px-3 py-1.5 bg-brand-50 text-slate-800 text-[12px] font-bold rounded-full">
                {loading ? '...' : `${keys.length} Keys`}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left whitespace-nowrap min-w-[480px]">
                <thead>
                  <tr className="text-[11px] font-bold text-slate-400 uppercase tracking-wide border-b border-slate-100">
                    <th className="px-5 sm:px-8 py-4">Name</th>
                    <th className="px-5 sm:px-8 py-4">Secret Key</th>
                    <th className="px-5 sm:px-8 py-4">Last Used</th>
                    <th className="px-5 sm:px-8 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    [1, 2].map((i) => (
                      <tr key={i}>
                        <td className="px-5 sm:px-8 py-5"><div className="h-5 w-32 bg-slate-100 rounded animate-pulse"></div></td>
                        <td className="px-5 sm:px-8 py-5"><div className="h-5 w-48 bg-slate-50 rounded animate-pulse"></div></td>
                        <td className="px-5 sm:px-8 py-5"><div className="h-5 w-24 bg-slate-50 rounded animate-pulse"></div></td>
                        <td className="px-5 sm:px-8 py-5 text-right"><div className="h-8 w-8 bg-slate-50 rounded ml-auto animate-pulse"></div></td>
                      </tr>
                    ))
                  ) : keys.length === 0 ? (
                    <tr><td colSpan={4} className="px-8 py-10 text-center text-slate-400 font-medium italic">No active API keys found.</td></tr>
                  ) : (
                    keys.map((key) => (
                      <tr key={key.id} className="hover:bg-slate-50 transition-colors group">
                        <td className="px-5 sm:px-8 py-5 text-[14px] font-bold text-slate-900">{key.name}</td>
                        <td className="px-5 sm:px-8 py-5">
                          <div className="flex items-center gap-2">
                            <code className="text-slate-400 text-[13px] font-mono tracking-wide">
                              {key.token.slice(0, 8)}{'••••••••••••'}{key.token.slice(-4)}
                            </code>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(key.token);
                                setCopiedKeyId(key.id);
                                setTimeout(() => setCopiedKeyId(null), 2000);
                              }}
                              className="text-slate-400 hover:text-slate-700 p-1 rounded transition-colors flex-shrink-0"
                              title="Copy API key"
                            >
                              {copiedKeyId === key.id
                                ? <ClipboardDocumentCheckIcon className="w-4 h-4 text-emerald-500" />
                                : <ClipboardDocumentIcon className="w-4 h-4" />}
                            </button>
                          </div>
                        </td>
                        <td className="px-5 sm:px-8 py-5 text-[13px] text-slate-500 font-medium">{formatDate(key.last_used_at)}</td>
                        <td className="px-5 sm:px-8 py-5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setAnalyticsKeyId(analyticsKeyId === key.id ? null : key.id)}
                              className={`px-3 py-1.5 rounded-lg text-[12px] font-bold transition-colors ${analyticsKeyId === key.id ? 'bg-brand-500 text-slate-900' : 'text-brand-700 hover:bg-brand-50'}`}
                            >
                              Analytics
                            </button>
                            <button onClick={() => setKeyToDelete(key)} className="text-slate-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 transition-colors"><TrashIcon className="w-5 h-5" /></button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Analytics panel */}
          {analyticsKeyId && (
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-[16px] font-bold text-slate-900">
                  Key Analytics, <span className="text-brand-700">{keys.find(k => k.id === analyticsKeyId)?.name}</span>
                </h3>
                <button onClick={() => setAnalyticsKeyId(null)} className="text-slate-400 hover:text-slate-700">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <AnalyticsPanel keyId={analyticsKeyId} />
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
            <div className="xl:col-span-8 flex flex-col gap-8">

              {/* API Documentation */}
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col overflow-hidden">
                <div className="px-4 sm:px-8 py-4 sm:py-6 border-b border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <DocumentTextIcon className="w-5 h-5 text-slate-400" />
                    <h3 className="text-[16px] font-bold text-slate-900">How to Use the API</h3>
                  </div>
                  <div className="flex bg-slate-100 rounded-xl p-1 gap-1 self-start sm:self-auto">
                    <button
                      onClick={() => setDocTab('single')}
                      className={`px-4 py-1.5 rounded-lg text-[12px] font-bold transition-colors ${docTab === 'single' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                      Single Search
                    </button>
                    <button
                      onClick={() => setDocTab('bulk')}
                      className={`px-4 py-1.5 rounded-lg text-[12px] font-bold transition-colors ${docTab === 'bulk' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                      Bulk CSV Upload
                    </button>
                  </div>
                </div>
                <div className="p-4 sm:p-8 flex flex-col gap-8">

                  {docTab === 'single' && (<>

                  {/* Plain-English flow */}
                  <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5 sm:p-6">
                    <p className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-4">How it works, 3 simple steps</p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      {[
                        { step: '1', label: 'Send a search', desc: 'Tell us the company name, location, and the job titles you want to find.' },
                        { step: '2', label: 'We run the search', desc: 'Our AI finds the right contacts. This takes a few seconds to a minute.' },
                        { step: '3', label: 'Get your results', desc: 'We send results to your URL automatically, or you can fetch them yourself.' },
                      ].map(s => (
                        <div key={s.step} className="flex-1 flex gap-3 items-start">
                          <div className="w-7 h-7 rounded-full bg-brand-500 text-slate-900 text-[12px] font-black flex items-center justify-center shrink-0">{s.step}</div>
                          <div>
                            <p className="text-[13px] font-black text-slate-900 mb-0.5">{s.label}</p>
                            <p className="text-[12px] text-slate-500 leading-relaxed">{s.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <p className="text-[12px] text-slate-500">
                        <span className="font-bold text-slate-700">Authentication:</span> Add your secret key to every request as a header named <code className="bg-white border border-slate-200 px-1.5 py-0.5 rounded text-[11px] font-mono">api_token</code>. Create a key above if you haven't yet.
                      </p>
                    </div>
                  </div>

                  {/* Step 1 — Start a search */}
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-[11px] font-black flex items-center justify-center shrink-0">1</div>
                      <h4 className="text-[15px] font-black text-slate-900">Start a search</h4>
                      <span className="px-2.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-black uppercase tracking-wide rounded-full">POST</span>
                    </div>
                    <p className="text-[13px] text-slate-500 ml-9 leading-relaxed">
                      Send a request with the company details you want to look up. You'll get back a <span className="font-bold text-slate-700">task ID</span>, think of it as a tracking number for your search.
                    </p>
                    <div className="ml-9 border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                      <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-100 flex items-center gap-2 overflow-x-auto">
                        <code className="text-[12px] font-mono text-slate-500">{baseURL}/api/v1/search</code>
                      </div>
                      <div className="p-4 flex flex-col gap-4">
                        <div>
                          <p className="text-[11px] font-black text-slate-400 uppercase tracking-wide mb-2">What to send</p>
                          <pre className="bg-[#0f172a] text-slate-300 p-4 rounded-xl text-[11px] font-mono overflow-auto">{`{
  "company":     "Stripe",           // Company name
  "address":    "USA",              // Address eg: 444 Francisco St San Francisco, CA 94133
  "titles":      ["CEO", "Founder"], // Job titles you want to find
  "depth":       1,                  // Search depth: 1 = standard
  "webhook_url": "https://..."       // Optional, see Step 3b
}`}</pre>
                        </div>
                        <div>
                          <p className="text-[11px] font-black text-slate-400 uppercase tracking-wide mb-2">What you get back</p>
                          <pre className="bg-[#0f172a] text-emerald-400 p-4 rounded-xl text-[11px] font-mono overflow-auto">{`{
  "task_id": "abc-123",   // Save this, you'll need it for Steps 2 & 3
  "status":  "queued"     // Search has started
}`}</pre>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Step 2 — Check status */}
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-black flex items-center justify-center shrink-0">2</div>
                      <h4 className="text-[15px] font-black text-slate-900">Check if it's done</h4>
                      <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase tracking-wide rounded-full">GET</span>
                    </div>
                    <p className="text-[13px] text-slate-500 ml-9 leading-relaxed">
                      Use the task ID from Step 1 to check whether your search is still running or has finished. Check every 3–5 seconds until <span className="font-bold text-slate-700">status = completed</span>. Skip this step if you used a webhook URL.
                    </p>
                    <div className="ml-9 border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                      <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-100 overflow-x-auto">
                        <code className="text-[12px] font-mono text-slate-500">{baseURL}/api/v1/tasks/<span className="text-brand-800">{'{task_id}'}</span></code>
                      </div>
                      <div className="p-4">
                        <p className="text-[11px] font-black text-slate-400 uppercase tracking-wide mb-2">What you get back</p>
                        <pre className="bg-[#0f172a] text-emerald-400 p-4 rounded-xl text-[11px] font-mono overflow-auto">{`{
  "id":         "abc-123",
  "status":     "completed",  // queued → processing → completed (or failed)
  "progress":   100,          // 0–100 percentage complete
  "results":    { "count": 4 }
}`}</pre>
                      </div>
                    </div>
                  </div>

                  {/* Step 3a — Fetch results */}
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-black flex items-center justify-center shrink-0">3a</div>
                      <h4 className="text-[15px] font-black text-slate-900">Fetch your contacts</h4>
                      <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase tracking-wide rounded-full">GET</span>
                    </div>
                    <p className="text-[13px] text-slate-500 ml-9 leading-relaxed">
                      Once status is <span className="font-bold text-slate-700">completed</span>, fetch the list of decision makers we found. Add <code className="bg-slate-100 px-1 rounded text-[11px] font-mono">?page=1&per_page=20</code> to page through large results.
                    </p>
                    <div className="ml-9 border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                      <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-100 overflow-x-auto">
                        <code className="text-[12px] font-mono text-slate-500">{baseURL}/api/v1/tasks/<span className="text-brand-800">{'{task_id}'}</span>/results</code>
                      </div>
                      <div className="p-4">
                        <p className="text-[11px] font-black text-slate-400 uppercase tracking-wide mb-2">What you get back</p>
                        <pre className="bg-[#0f172a] text-emerald-400 p-4 rounded-xl text-[11px] font-mono overflow-auto">{`{
  "data": [
    {
      "name":            "Patrick Collison", // Full name
      "title":           "CEO",              // Job title
      "company_name":    "Stripe",
      "company_domain":  "stripe.com",       // Website
      "company_type":    "Fintech",          // Industry category
      "company_address": "San Francisco, USA"
    }
  ],
  "meta": {
    "total":     4,   // Total contacts found
    "page":      1,
    "per_page":  20,
    "last_page": 1
  }
}`}</pre>
                      </div>
                    </div>
                  </div>

                  {/* Step 3b — Webhook */}
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-[11px] font-black flex items-center justify-center shrink-0">3b</div>
                      <h4 className="text-[15px] font-black text-slate-900">Or, receive results automatically</h4>
                      <span className="px-2.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-black uppercase tracking-wide rounded-full">WEBHOOK</span>
                    </div>
                    <p className="text-[13px] text-slate-500 ml-9 leading-relaxed">
                      If you included a <span className="font-bold text-slate-700">webhook_url</span> in Step 1, we'll automatically send the results to that URL the moment your search finishes, no need to keep checking. Just make sure your server responds with a <span className="font-bold text-slate-700">200 OK</span>. We'll retry up to 3 times if your server doesn't respond.
                    </p>
                    <div className="ml-9 border border-amber-100 bg-amber-50/30 rounded-xl overflow-hidden shadow-sm">
                      <div className="bg-amber-50 px-4 py-2.5 border-b border-amber-100">
                        <p className="text-[12px] text-amber-700 font-bold">We POST this to your webhook_url</p>
                      </div>
                      <div className="p-4">
                        <pre className="bg-[#0f172a] text-slate-300 p-4 rounded-xl text-[11px] font-mono overflow-auto">{`{
  "task_id":      "abc-123",
  "status":       "completed",
  "company":      "Stripe",
  "location":     "USA",
  "leads_count":  4,              // How many contacts were found
  "completed_at": "2026-06-09T12:00:00+00:00",
  "leads": [
    {
      "name":            "Patrick Collison",
      "title":           "CEO",
      "company_name":    "Stripe",
      "company_domain":  "stripe.com",
      "company_type":    "Fintech",
      "company_address": "San Francisco, USA"
    }
  ]
}`}</pre>
                      </div>
                    </div>
                  </div>

                  {/* Browse all tasks */}
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 text-[11px] font-black flex items-center justify-center shrink-0">+</div>
                      <h4 className="text-[15px] font-black text-slate-900">Browse all your past searches</h4>
                      <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase tracking-wide rounded-full">GET</span>
                    </div>
                    <p className="text-[13px] text-slate-500 ml-9 leading-relaxed">
                      List all searches you've ever run, with optional page navigation. Useful for building a history view in your own app.
                    </p>
                    <div className="ml-9 border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                      <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-100 overflow-x-auto">
                        <code className="text-[12px] font-mono text-slate-500">{baseURL}/api/v1/tasks<span className="text-slate-400">?page=1&per_page=20</span></code>
                      </div>
                    </div>
                  </div>

                  </>)}

                  {docTab === 'bulk' && (<>

                  {/* Bulk — How it works */}
                  <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5 sm:p-6">
                    <p className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-4">How bulk search works</p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      {[
                        { step: '1', label: 'Upload your CSV', desc: 'Send a CSV file with company names. We auto-detect the company and location columns.' },
                        { step: '2', label: 'We process all rows', desc: 'Our workers search each company in parallel. Large files can take a few minutes.' },
                        { step: '3', label: 'Download results', desc: 'Fetch all contacts as paginated JSON or download the full CSV in one request.' },
                      ].map(s => (
                        <div key={s.step} className="flex-1 flex gap-3 items-start">
                          <div className="w-7 h-7 rounded-full bg-brand-500 text-slate-900 text-[12px] font-black flex items-center justify-center shrink-0">{s.step}</div>
                          <div>
                            <p className="text-[13px] font-black text-slate-900 mb-0.5">{s.label}</p>
                            <p className="text-[12px] text-slate-500 leading-relaxed">{s.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <p className="text-[12px] text-slate-500">
                        <span className="font-bold text-slate-700">Authentication:</span> Send your secret key as a header named <code className="bg-white border border-slate-200 px-1.5 py-0.5 rounded text-[11px] font-mono">api_token</code>. The upload endpoint uses <code className="bg-white border border-slate-200 px-1.5 py-0.5 rounded text-[11px] font-mono">multipart/form-data</code>.
                      </p>
                    </div>
                  </div>

                  {/* Bulk Step 1 — Upload CSV */}
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-[11px] font-black flex items-center justify-center shrink-0">1</div>
                      <h4 className="text-[15px] font-black text-slate-900">Upload your CSV</h4>
                      <span className="px-2.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-black uppercase tracking-wide rounded-full">POST</span>
                      <span className="px-2.5 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-black uppercase tracking-wide rounded-full">multipart/form-data</span>
                    </div>
                    <p className="text-[13px] text-slate-500 ml-9 leading-relaxed">
                      Send a CSV with a company column and optionally a location column. We auto-detect columns named "company" and "location" — override with <code className="bg-slate-100 px-1 rounded text-[11px] font-mono">company_column</code> / <code className="bg-slate-100 px-1 rounded text-[11px] font-mono">location_column</code> if your CSV uses different names.
                    </p>
                    <div className="ml-9 border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                      <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-100 flex items-center gap-2 overflow-x-auto">
                        <code className="text-[12px] font-mono text-slate-500">{baseURL}/api/v1/bulk/upload</code>
                      </div>
                      <div className="p-4 flex flex-col gap-4">
                        <div>
                          <p className="text-[11px] font-black text-slate-400 uppercase tracking-wide mb-2">Form fields</p>
                          <pre className="bg-[#0f172a] text-slate-300 p-4 rounded-xl text-[11px] font-mono overflow-auto">{`file             (required)  Your CSV file — max 20 MB
titles[]         (required)  e.g. titles[]=CEO&titles[]=Founder
depth            (required)  Search depth 1–8 (1 = standard)
company_column   (optional)  Column with company names — default: auto-detect
location_column  (optional)  Column with locations — default: auto-detect
skip_previous    (optional)  true = skip companies searched in last 30 days
file_name        (optional)  Label for this job e.g. "Q3 Leads"
webhook_url      (optional)  We POST here when the job completes`}</pre>
                        </div>
                        <div>
                          <p className="text-[11px] font-black text-slate-400 uppercase tracking-wide mb-2">What you get back</p>
                          <pre className="bg-[#0f172a] text-emerald-400 p-4 rounded-xl text-[11px] font-mono overflow-auto">{`{
  "task_id":             "abc-123",   // Use this for all following requests
  "total_companies":     245,         // Companies that will be searched
  "skipped_no_company":  3,           // Rows skipped — company name missing
  "skipped_no_location": 1,           // Warning: location missing — rows still searched globally
  "skipped_previous":    12,          // Rows skipped — searched in last 30 days
  "status":              "pending",
  "message":             "Bulk search job queued successfully"
}`}</pre>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bulk Step 2 — Track status */}
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-black flex items-center justify-center shrink-0">2</div>
                      <h4 className="text-[15px] font-black text-slate-900">Track job progress</h4>
                      <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase tracking-wide rounded-full">GET</span>
                    </div>
                    <p className="text-[13px] text-slate-500 ml-9 leading-relaxed">
                      Poll every 15–30 seconds. The <span className="font-bold text-slate-700">progress</span> field (0–100) tracks completion. When status = <span className="font-bold text-slate-700">completed</span>, your results are ready.
                    </p>
                    <div className="ml-9 border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                      <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-100 overflow-x-auto">
                        <code className="text-[12px] font-mono text-slate-500">{baseURL}/api/v1/bulk/tasks/<span className="text-brand-800">{'{task_id}'}</span></code>
                      </div>
                      <div className="p-4">
                        <pre className="bg-[#0f172a] text-emerald-400 p-4 rounded-xl text-[11px] font-mono overflow-auto">{`{
  "id":                "abc-123",
  "status":            "processing",  // pending → processing → completed (or failed)
  "progress":          62,            // 0–100 percentage complete
  "total_records":     245,
  "processed_records": 152
}`}</pre>
                      </div>
                    </div>
                  </div>

                  {/* Bulk Step 3a — JSON results */}
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-black flex items-center justify-center shrink-0">3a</div>
                      <h4 className="text-[15px] font-black text-slate-900">Fetch contacts as JSON</h4>
                      <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase tracking-wide rounded-full">GET</span>
                    </div>
                    <p className="text-[13px] text-slate-500 ml-9 leading-relaxed">
                      Once completed, page through all found contacts. Add <code className="bg-slate-100 px-1 rounded text-[11px] font-mono">?page=1&per_page=100</code> for large result sets.
                    </p>
                    <div className="ml-9 border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                      <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-100 overflow-x-auto">
                        <code className="text-[12px] font-mono text-slate-500">{baseURL}/api/v1/bulk/tasks/<span className="text-brand-800">{'{task_id}'}</span>/results</code>
                      </div>
                      <div className="p-4">
                        <pre className="bg-[#0f172a] text-emerald-400 p-4 rounded-xl text-[11px] font-mono overflow-auto">{`{
  "data": [
    {
      "name":            "Patrick Collison",
      "title":           "CEO",
      "company_name":    "Stripe",
      "company_domain":  "stripe.com",
      "company_type":    "Fintech",
      "company_address": "San Francisco, USA"
    }
  ],
  "meta": { "total": 840, "page": 1, "per_page": 20, "last_page": 42 }
}`}</pre>
                      </div>
                    </div>
                  </div>

                  {/* Bulk Step 3b — Download CSV */}
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-[11px] font-black flex items-center justify-center shrink-0">3b</div>
                      <h4 className="text-[15px] font-black text-slate-900">Or, download the full CSV</h4>
                      <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase tracking-wide rounded-full">GET</span>
                    </div>
                    <p className="text-[13px] text-slate-500 ml-9 leading-relaxed">
                      Get all results as a ready-to-open CSV — columns: Company Name, Domain, Type, Location, Person Name, Job Title.
                    </p>
                    <div className="ml-9 border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                      <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-100 overflow-x-auto">
                        <code className="text-[12px] font-mono text-slate-500">{baseURL}/api/v1/bulk/tasks/<span className="text-brand-800">{'{task_id}'}</span>/download</code>
                      </div>
                      <div className="p-4">
                        <p className="text-[12px] text-slate-500">Returns a <code className="bg-slate-100 px-1 rounded text-[11px] font-mono">text/csv</code> file. Add <code className="bg-slate-100 px-1 rounded text-[11px] font-mono">-o results.csv</code> to your cURL command to save it to disk.</p>
                      </div>
                    </div>
                  </div>

                  {/* Bulk — Browse jobs */}
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 text-[11px] font-black flex items-center justify-center shrink-0">+</div>
                      <h4 className="text-[15px] font-black text-slate-900">List all your bulk jobs</h4>
                      <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase tracking-wide rounded-full">GET</span>
                    </div>
                    <div className="ml-9 border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                      <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-100 overflow-x-auto">
                        <code className="text-[12px] font-mono text-slate-500">{baseURL}/api/v1/bulk/tasks</code>
                      </div>
                    </div>
                  </div>

                  </>)}

                </div>
              </div>

              {/* Individual Key Logs */}
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col overflow-hidden min-h-[500px]">
                <div className="px-4 sm:px-8 py-4 sm:py-6 border-b border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex flex-col">
                    <h3 className="text-[16px] font-bold text-slate-900">Request Logs</h3>
                    <span className="text-[12px] font-medium text-slate-500">Live traffic inspector</span>
                  </div>
                  <div className="relative">
                    <select
                      value={filterKeyId}
                      onChange={(e) => setFilterKeyId(e.target.value)}
                      className="bg-white border border-slate-200 text-slate-700 text-[13px] font-bold pl-4 pr-10 py-2.5 rounded-xl outline-none focus:ring-2 focus:ring-brand-500/20 appearance-none w-full sm:min-w-[200px] cursor-pointer"
                    >
                      <option value="all">All Active Keys</option>
                      {keys.map(key => <option key={key.id} value={key.id}>{key.name}</option>)}
                    </select>
                    <ChevronDownIcon className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left whitespace-nowrap min-w-[360px]">
                    <thead>
                      <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wide border-b border-slate-100">
                        <th className="px-4 sm:px-8 py-4">Status</th>
                        <th className="px-4 sm:px-8 py-4">Endpoint</th>
                        <th className="px-4 sm:px-8 py-4">Key</th>
                        <th className="px-4 sm:px-8 py-4 text-right">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {loading ? (
                        [1, 2, 3].map(i => (
                           <tr key={i}>
                            <td colSpan={4} className="px-4 sm:px-8 py-4"><div className="h-6 w-full bg-slate-50 rounded animate-pulse"></div></td>
                           </tr>
                        ))
                      ) : filteredLogs.length === 0 ? (
                        <tr><td colSpan={4} className="px-4 sm:px-8 py-16 text-center text-slate-400 text-sm font-medium italic">No logs recorded for the selected key.</td></tr>
                      ) : (
                        filteredLogs.map((log) => (
                          <React.Fragment key={log.id}>
                            <tr className="hover:bg-slate-50 transition-colors">
                              <td className="px-4 sm:px-8 py-4">
                                <span className={`px-2 py-1 rounded text-[11px] font-extrabold ${log.status_code < 300 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                  {log.status_code}
                                </span>
                              </td>
                              <td className="px-4 sm:px-8 py-4 font-mono text-[13px] font-bold text-slate-700">/{log.endpoint}</td>
                              <td className="px-4 sm:px-8 py-4 text-[12px] font-bold text-slate-500">{log.api_key?.name || 'Deleted'}</td>
                              <td className="px-4 sm:px-8 py-4 text-right">
                                <button onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)} className="text-brand-700 font-extrabold text-[12px] hover:text-brand-900">
                                  {expandedLogId === log.id ? 'Hide' : 'Inspect'}
                                </button>
                              </td>
                            </tr>
                            {expandedLogId === log.id && (
                              <tr className="bg-slate-50 shadow-inner">
                                <td colSpan={4} className="p-6">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex flex-col gap-2">
                                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-wide">Request Payload</span>
                                      <pre className="bg-[#0f172a] text-emerald-400 p-4 rounded-2xl text-[11px] overflow-auto max-h-60 custom-scrollbar">{JSON.stringify(log.request_payload, null, 2)}</pre>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-wide">Response Body</span>
                                      <pre className="bg-[#0f172a] text-slate-300 p-4 rounded-2xl text-[11px] overflow-auto max-h-60 custom-scrollbar">{JSON.stringify(log.response_payload, null, 2)}</pre>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Pagination Controls */}
                {totalLogs > 0 && (
                  <div className="px-4 sm:px-8 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between gap-4 flex-wrap">
                    <span className="text-[13px] font-medium text-slate-600">
                      Page {currentPage} of {lastPage} • {totalLogs} total request{totalLogs !== 1 ? 's' : ''}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        className="flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-[12px] font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                      >
                        <ChevronLeftIcon className="w-4 h-4" /> Previous
                      </button>
                      <button
                        onClick={handleNextPage}
                        disabled={currentPage >= lastPage}
                        className="flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-[12px] font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                      >
                        Next <ChevronLeftIcon className="w-4 h-4 rotate-180" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Side Panel */}
            <div className="xl:col-span-4 flex flex-col gap-8">
              <div className="flex flex-col gap-5 h-fit sticky top-6">
                {/* Quick tips */}
                <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                  <p className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-4">Good to know</p>
                  <div className="flex flex-col gap-4">
                    {([
                      {
                        icon: (
                          <svg className="w-4 h-4 text-brand-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                          </svg>
                        ),
                        title: 'Your secret key',
                        body: 'Every request needs your API key sent as a header called api_token. Never share it publicly.',
                      },
                      {
                        icon: (
                          <svg className="w-4 h-4 text-brand-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                          </svg>
                        ),
                        title: '1 credit per search',
                        body: 'Each company you search costs 1 credit, regardless of how many contacts are found.',
                      },
                      {
                        icon: (
                          <svg className="w-4 h-4 text-brand-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        ),
                        title: 'Searches take a moment',
                        body: 'Most searches finish in under 60 seconds. Use a webhook for the best experience.',
                      },
                      {
                        icon: (
                          <svg className="w-4 h-4 text-brand-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                          </svg>
                        ),
                        title: 'Webhooks are optional',
                        body: 'If you skip the webhook_url, just poll the status endpoint every few seconds instead.',
                      },
                    ] as { icon: React.ReactNode; title: string; body: string }[]).map(tip => (
                      <div key={tip.title} className="flex gap-3 items-start">
                        <div className="w-7 h-7 rounded-lg bg-brand-50 flex items-center justify-center shrink-0">{tip.icon}</div>
                        <div>
                          <p className="text-[13px] font-black text-slate-800 mb-0.5">{tip.title}</p>
                          <p className="text-[12px] text-slate-500 leading-relaxed">{tip.body}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* cURL example */}
                <div className="bg-[#0f172a] rounded-2xl p-5 shadow-xl">
                  <div className="flex items-center gap-2.5 mb-3">
                    <CommandLineIcon className="w-4 h-4 text-slate-400" />
                    <p className="text-[12px] font-black text-slate-400 uppercase tracking-widest">
                      {docTab === 'single' ? 'Example request' : 'Example bulk upload'}
                    </p>
                  </div>
                  <div className="bg-[#1e293b] rounded-xl p-4 border border-slate-700 shadow-inner overflow-x-auto mb-4">
                    {docTab === 'single' ? (
                      <pre className="text-[11px] font-mono text-slate-300 leading-relaxed whitespace-pre-wrap break-all">
                        <span className="text-pink-400">curl</span>{` -X POST \\`}{"\n"}
                        {"  "}<span className="text-amber-300">{baseURL}/api/v1/search</span>{` \\`}{"\n"}
                        {"  "}-H <span className="text-emerald-300">"api_token: YOUR_KEY"</span>{` \\`}{"\n"}
                        {"  "}-d <span className="text-emerald-300">{`'{"company":"Stripe","address":"USA","titles":["CEO"],"depth":1}'`}</span>
                      </pre>
                    ) : (
                      <pre className="text-[11px] font-mono text-slate-300 leading-relaxed whitespace-pre-wrap break-all">
                        <span className="text-pink-400">curl</span>{` -X POST \\`}{"\n"}
                        {"  "}<span className="text-amber-300">{baseURL}/api/v1/bulk/upload</span>{` \\`}{"\n"}
                        {"  "}-H <span className="text-emerald-300">"api_token: YOUR_KEY"</span>{` \\`}{"\n"}
                        {"  "}-F <span className="text-emerald-300">"file=@companies.csv"</span>{` \\`}{"\n"}
                        {"  "}-F <span className="text-emerald-300">"titles[]=CEO"</span>{` \\`}{"\n"}
                        {"  "}-F <span className="text-emerald-300">"titles[]=Founder"</span>{` \\`}{"\n"}
                        {"  "}-F <span className="text-emerald-300">"depth=1"</span>
                      </pre>
                    )}
                  </div>
                  <div className="flex flex-col gap-2.5">
                    <div className="flex items-start gap-2.5 text-[12px] text-slate-400">
                      <CheckCircleIcon className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      Replace <code className="bg-slate-800 px-1 rounded text-[11px]">YOUR_KEY</code> with a key from the table above
                    </div>
                    <div className="flex items-start gap-2.5 text-[12px] text-slate-400">
                      <CheckCircleIcon className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      All requests are logged, check the Request Logs below
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CREATE DIALOG */}
      <Dialog.Root open={isCreateOpen} onOpenChange={handleCreateClose}>
        <Dialog.Portal>
          <Dialog.Overlay className="dialog-overlay" />
          <Dialog.Content className="dialog-content">
            {!newlyCreatedToken ? (
              <>
                <Dialog.Title className="text-[20px] font-extrabold text-slate-900 tracking-tight mb-2">Create Secret Key</Dialog.Title>
                <Dialog.Description className="text-[14px] text-slate-500 font-medium mb-6">Give your key a descriptive name to identify it later.</Dialog.Description>
                <input 
                  type="text" placeholder="e.g. CRM Integration" 
                  value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 text-slate-900 px-5 py-3.5 rounded-2xl mb-6 outline-none focus:ring-2 focus:ring-brand-500/20 font-bold"
                />
                <div className="flex justify-end gap-3">
                  <Dialog.Close className="px-5 py-2.5 rounded-xl font-bold text-slate-500 hover:bg-slate-50">Cancel</Dialog.Close>
                  <button onClick={handleCreateKey} disabled={!newKeyName} className="bg-brand-500 text-slate-900 px-6 py-2.5 rounded-xl font-bold hover:bg-brand-600 disabled:opacity-50 transition-colors">Generate Key</button>
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-col items-center text-center">
                   <div className="w-16 h-16 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mb-4"><CheckCircleIcon className="w-8 h-8" /></div>
                   <Dialog.Title className="text-[20px] font-extrabold text-emerald-800 mb-2">Key Generated!</Dialog.Title>
                   <Dialog.Description className="text-[14px] text-emerald-600 font-medium mb-6">Copy this key now. It will not be shown again for security reasons.</Dialog.Description>
                </div>
                <code className="block bg-emerald-50 border border-emerald-200 p-4 rounded-xl text-emerald-900 font-mono text-[13px] mb-6 break-all tracking-wider text-center font-bold">{newlyCreatedToken}</code>
                <button onClick={() => handleCopy(newlyCreatedToken)} className="w-full bg-emerald-600 text-slate-900 py-3.5 rounded-xl font-bold mb-3 transition-all hover:bg-emerald-700 shadow-lg shadow-emerald-500/20">
                  {copied ? 'Copied to Clipboard!' : 'Copy to Clipboard'}
                </button>
                <Dialog.Close className="w-full py-2 font-bold text-slate-400 hover:text-slate-600">Close Window</Dialog.Close>
              </>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* DELETE DIALOG */}
      <Dialog.Root open={!!keyToDelete} onOpenChange={(open) => !open && setKeyToDelete(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="dialog-overlay" />
          <Dialog.Content className="dialog-content">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mb-4"><ExclamationTriangleIcon className="w-8 h-8" /></div>
              <Dialog.Title className="text-[20px] font-extrabold text-slate-900 mb-2">Revoke API Key?</Dialog.Title>
              <Dialog.Description className="text-[14px] text-slate-500 font-medium mb-8">
                Are you sure you want to delete <span className="font-extrabold text-slate-800">{keyToDelete?.name}</span>? Applications using this key will fail immediately.
              </Dialog.Description>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Dialog.Close className="py-3.5 rounded-xl font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors">Cancel</Dialog.Close>
              <button onClick={handleDeleteKey} className="bg-red-500 text-slate-900 py-3.5 rounded-xl font-bold hover:bg-red-600 transition-all shadow-lg shadow-red-500/25">Revoke Key</button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}